import type { DatasetUploadJob } from "@/api";
import { TableRow, type TableRowActionBuilder } from "@carbon6/c6-frontend-design";

export type DatasetUploadTableRowActionBuilder = TableRowActionBuilder<DatasetUploadTableRow>;
export type DatasetUploadTableRowClickHandler = (row: DatasetUploadTableRow) => void;

export class DatasetUploadTableRow extends TableRow implements DatasetUploadJob {
  readonly uuid: string;
  readonly organization_uuid: string;
  readonly created_by_member_uuid: string | null;
  readonly artifact_source_uuid: string | null;
  readonly dataset_uuid: string | null;
  readonly embodiment_uuid: string | null;
  readonly embodiment: DatasetUploadJob["embodiment"];
  readonly tip_uuid: string | null;
  readonly tip: DatasetUploadJob["tip"];
  readonly display_name: string;
  readonly target_backend: DatasetUploadJob["target_backend"];
  readonly target_object_key_prefix: string | null;
  readonly staging_object_key: string;
  readonly status: DatasetUploadJob["status"];
  readonly publish_progress_percent: number | null;
  readonly import_progress: DatasetUploadJob["import_progress"];
  readonly detected_schema_version: DatasetUploadJob["detected_schema_version"];
  readonly migration_description: string | null;
  readonly failure: DatasetUploadJob["failure"];
  readonly declared_size_bytes: number;
  readonly staged_file_count: number | null;
  readonly missing_channel_episode_count: number | null;
  readonly upload_verified: boolean;
  readonly reserved_labels: DatasetUploadJob["reserved_labels"];
  readonly data_origin: DatasetUploadJob["data_origin"];
  readonly band_diagnostics_enabled: DatasetUploadJob["band_diagnostics_enabled"];
  readonly lease_active: boolean;
  readonly lease_expires_at: string | null;
  readonly owned_by_me: boolean;
  readonly created_at: string;
  readonly modified_at: string;
  private readonly rowClickHandler: DatasetUploadTableRowClickHandler | null;

  constructor(
    job: DatasetUploadJob,
    rowActionsBuilder: DatasetUploadTableRowActionBuilder | null = null,
    rowClickHandler: DatasetUploadTableRowClickHandler | null = null,
  ) {
    super(rowActionsBuilder ? (row) => rowActionsBuilder(row as DatasetUploadTableRow) : null);
    this.rowClickHandler = rowClickHandler;
    this.uuid = job.uuid;
    this.organization_uuid = job.organization_uuid;
    this.created_by_member_uuid = job.created_by_member_uuid;
    this.artifact_source_uuid = job.artifact_source_uuid;
    this.dataset_uuid = job.dataset_uuid;
    this.embodiment_uuid = job.embodiment_uuid;
    this.embodiment = job.embodiment;
    this.tip_uuid = job.tip_uuid;
    this.tip = job.tip;
    this.display_name = job.display_name;
    this.target_backend = job.target_backend;
    this.target_object_key_prefix = job.target_object_key_prefix;
    this.staging_object_key = job.staging_object_key;
    this.status = job.status;
    this.publish_progress_percent = job.publish_progress_percent;
    this.import_progress = job.import_progress;
    this.detected_schema_version = job.detected_schema_version;
    this.migration_description = job.migration_description;
    this.failure = job.failure;
    this.declared_size_bytes = job.declared_size_bytes;
    this.staged_file_count = job.staged_file_count;
    this.missing_channel_episode_count = job.missing_channel_episode_count;
    this.upload_verified = job.upload_verified;
    this.reserved_labels = job.reserved_labels;
    this.data_origin = job.data_origin;
    this.band_diagnostics_enabled = job.band_diagnostics_enabled;
    this.lease_active = job.lease_active;
    this.lease_expires_at = job.lease_expires_at;
    this.owned_by_me = job.owned_by_me;
    this.created_at = job.created_at;
    this.modified_at = job.modified_at;
  }

  getRowId(): string {
    return this.uuid;
  }

  // 모든 행을 표시한다. 가시성은 서버 조회가 정한다.
  canShow(): boolean {
    return true;
  }

  // 선택 UI 가 없는 목록이므로 선택 대상이 아니다.
  canSelect(): boolean {
    return false;
  }

  get isRowClickable(): boolean {
    // 발행이 완료된(done) 데이터셋만 리뷰 상세로 열 수 있다. uploaded 같은 처리 중 상태는 클릭을 막는다.
    return this.rowClickHandler !== null && this.dataset_uuid !== null && this.status === "done";
  }

  onRowClick(): void {
    this.rowClickHandler?.(this);
  }
}

export function createDatasetUploadTableRow(
  job: DatasetUploadJob,
  rowActionsBuilder: DatasetUploadTableRowActionBuilder | null = null,
  rowClickHandler: DatasetUploadTableRowClickHandler | null = null,
): DatasetUploadTableRow {
  return new DatasetUploadTableRow(job, rowActionsBuilder, rowClickHandler);
}

export function createDatasetUploadTableRows(
  jobs: readonly DatasetUploadJob[],
  rowActionsBuilder: DatasetUploadTableRowActionBuilder | null = null,
  rowClickHandler: DatasetUploadTableRowClickHandler | null = null,
): DatasetUploadTableRow[] {
  return jobs.map((job) => createDatasetUploadTableRow(job, rowActionsBuilder, rowClickHandler));
}
